import api from 'services/api';

export type AddressBase = {
  code: string;
  siteId: string;
  addressLine?: string;
  addressNumber?: string;
  complement?: string;
  city?: string;
  stateCode?: string;
};

export type AddressCustomer = {
  customerCode: string;
  customerId: string;
  name: string;
  document: string;
  userCustomerRelationshipId: string;
  customerAddress: AddressBase[];
};

export type UserAddress = {
  base: AddressBase;
  address: AddressCustomer;
};

export const PENDING_KEY = '@UltragazEmpresarial:selecaoEnderecoPendente';

export const marcarSelecaoPendente = (): void => {
  sessionStorage.setItem(PENDING_KEY, 'true');
};

export const selecaoPendente = (): boolean =>
  sessionStorage.getItem(PENDING_KEY) === 'true';

export const limparSelecaoPendente = (): void => {
  sessionStorage.removeItem(PENDING_KEY);
};

const headers = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  access_token: localStorage.getItem('@UltragazEmpresarial:accessToken') ?? '',
});

export const getUserId = async (login: string): Promise<string> => {
  const response = await api.get(
    `industrial/customer/v1/users/?login=${login}&is-active=true`,
    { headers: headers() }
  );

  return response.data?.[0]?.userId ?? '';
};

export const getAddresses = async (userId: string): Promise<UserAddress[]> => {
  const response = await api.get(
    `industrial/customer/v1/users/${userId}/customers?&_offset=0&_limit=1000&address_type=SHIP_TO`,
    { headers: headers() }
  );

  const lista: UserAddress[] = [];

  (response.data ?? []).forEach((address: AddressCustomer) => {
    (address.customerAddress ?? []).forEach((base) => {
      lista.push({ base, address });
    });
  });

  return lista.sort((a, b) => a.address.name.localeCompare(b.address.name));
};

export const changeProfile = async (
  userCustomerRelationshipId: string,
  username: string
): Promise<string> => {
  const response = await api.post(
    'industrial/customer/v1/users/change-profile',
    { userCustomerRelationshipId, username },
    { headers: headers() }
  );

  return response.data?.access_token ?? '';
};

export const persistSelection = ({ base, address }: UserAddress): void => {
  localStorage.setItem('@UltragazEmpresarial:customerSiteCode', base.code);
  localStorage.setItem('@UltragazEmpresarial:customerSiteId', base.siteId);
  localStorage.setItem(
    '@UltragazEmpresarial:customerCode',
    address.customerCode
  );
  localStorage.setItem('@UltragazEmpresarial:customerId', address.customerId);
  localStorage.setItem('@UltragazEmpresarial:customerName', address.name);
  localStorage.setItem('@UltragazEmpresarial:document', address.document);
  localStorage.setItem(
    '@UltragazEmpresarial:customerAddressLine',
    [base.addressLine, base.addressNumber].filter(Boolean).join(', ')
  );
  localStorage.setItem(
    '@UltragazEmpresarial:userCustomerRelationshipId',
    address.userCustomerRelationshipId
  );
  localStorage.setItem('@UltragazEmpresarial:city', base.city ?? '');
  localStorage.setItem('@UltragazEmpresarial:stateCode', base.stateCode ?? '');
  localStorage.removeItem('twilio-flex-cf');
};

export const syncContactIdSf = async (siteId: string): Promise<void> => {
  const loginEmail = localStorage.getItem('@UltragazEmpresarial:loginEmail');

  const response = await api.get('industrial/customer/v1/customers/me', {
    headers: headers(),
  });

  const endereco = (response.data?.customerAddresses ?? []).find(
    (customerAddress: { addressType: string; siteId: string }) =>
      customerAddress.addressType === 'SHIP_TO' &&
      customerAddress.siteId === siteId
  );

  const contactIdSf = (endereco?.contacts ?? []).find(
    (contact: { contact: string }) => contact.contact === loginEmail
  )?.contactIdSf;

  if (contactIdSf) {
    localStorage.setItem('@UltragazEmpresarial:contactIdSf', contactIdSf);
  }
};

export const descreverEndereco = (base: AddressBase): string =>
  [
    [base.addressLine, base.addressNumber].filter(Boolean).join(', '),
    base.complement,
    base.city,
    base.stateCode,
  ]
    .filter(Boolean)
    .join(' - ');

export const idDoEndereco = ({ base, address }: UserAddress): string =>
  `${address.userCustomerRelationshipId}:${base.siteId}`;
